<template>
  <WdsStack direction-column gap-sm class="tw-mt-2" data-test="restricted-document-decision">
    <WdsText
      v-if="approvedAt"
      :text="$t('Approved :date', { date: approvedDate })"
      success
      sm
      data-test="restricted-document-approved"
    />
    <WdsText
      v-else-if="rejectionReason"
      :text="$t('Declined: :reason', { reason: rejectionReason })"
      error
      sm
      data-test="restricted-document-declined"
    />

    <template v-if="declining">
      <WdsInputArea
        v-model="reason"
        :label="$t('Reason for declining')"
        :height="'72px'"
        resizeable
        data-test="restricted-document-decline-reason"
      />
      <WdsStack direction-row justify-space-between gap-sm>
        <WdsButton tertiary :disabled="submitting" @click="declining = false">
          {{ $t('Cancel') }}
        </WdsButton>
        <WdsButton
          primary
          :disabled="!canConfirmDecline"
          data-test="restricted-document-confirm-decline"
          @click="decide(false)"
        >
          <WdsSpinner v-if="submitting" :size="14" indeterminate />
          <template v-else>{{ $t('Confirm decline') }}</template>
        </WdsButton>
      </WdsStack>
    </template>

    <WdsStack v-else direction-row justify-space-between gap-sm>
      <WdsButton tertiary :disabled="submitting" @click="$emit('close')">{{ $t('Close') }}</WdsButton>

      <!--
        Approval is final in this UI: a mistaken approval is corrected by the
        worker uploading a replacement (which supersedes this document and starts
        a fresh review), not by declining the approved evidence.
      -->
      <WdsStack v-if="!approvedAt" direction-row gap-sm>
        <WdsButton
          v-if="!rejectionReason"
          tertiary
          :disabled="submitting"
          data-test="restricted-document-decline"
          @click="declining = true"
        >
          {{ $t('Decline') }}
        </WdsButton>
        <WdsButton primary :disabled="submitting" data-test="restricted-document-approve" @click="decide(true)">
          <WdsSpinner v-if="submitting" :size="14" indeterminate />
          <template v-else>{{ $t('Approve') }}</template>
        </WdsButton>
      </WdsStack>
    </WdsStack>

    <WdsAlert v-if="errorMessage" error data-test="restricted-document-error">{{ errorMessage }}</WdsAlert>
  </WdsStack>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { WdsAlert, WdsButton, WdsInputArea, WdsSpinner, WdsStack, WdsText } from '@worksome/wds-components'
import { graphql } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { formatDocumentDate } from '@/utils/restrictedDocument'

// Same shape as ComplianceReviewDecision: the mutation lives here and the parent only learns that a decision landed.
const props = defineProps<{
  documentId: string
  approvedAt: string | null
  rejectionReason: string | null
}>()

const emit = defineEmits<{ close: []; decided: [] }>()

const ReviewRightToWorkDocumentMutation = graphql(`
  mutation ReviewRightToWorkDocument($input: ReviewRightToWorkDocumentInput!) {
    reviewRightToWorkDocument(input: $input) {
      document {
        id
        label
        filename
        expiresAt
        viewUrl
      }
      status
      approvedAt
      rejectionReason
      canReview
    }
  }
`)

const {
  mutate: reviewDocument,
  loading: submitting,
  error: mutationError,
} = useMutation(ReviewRightToWorkDocumentMutation, {
  clientId: 'publicApi',
  refetchQueries: ['ComplianceTrustedContactsTable', 'ComplianceReviewModal'],
  throws: 'never',
})

const declining = ref(false)
const reason = ref('')

const errorMessage = computed(() => mutationError.value?.graphQLErrors?.[0]?.message ?? '')
const canConfirmDecline = computed(() => reason.value.trim().length >= 3 && !submitting.value)
const approvedDate = computed(() => formatDocumentDate(props.approvedAt))

async function decide(approved: boolean) {
  // Omitted entirely on approval: the field is required for declines, and an explicit null would trip that.
  const input: { id: string; approved: boolean; rejectionReason?: string } = { id: props.documentId, approved }
  if (!approved) input.rejectionReason = reason.value.trim()

  const result = await reviewDocument({ input })

  if (result?.data?.reviewRightToWorkDocument) {
    declining.value = false
    reason.value = ''
    emit('decided')
  }
}
</script>
